import {NEW_ID} from '../../../../common/constants/index.js'
import ButtonPrimary from '../../../../common/components/Button/ButtonPrimary.js'
import ButtonSecondary from '../../../../common/components/Button/ButtonSecondary.js'
import Nickname from './Nickname.js'
import ShippingMethods from './ShippingMethods.js'
import {
  formSelector,
  getState,
  onlyIfAutoForm,
  updateForm,
  useSelector,
} from '../../../../store.js'
import {showGlobalError} from '../../../GlobalErrorMessage.js'
import {PITNEY_CBDS} from '../../../../common/constants/ShipperNames.js'
import api from '../../../../common/api.js'
import {setShipper} from '../../../../data/shippers.js'
import {isPresent} from '../../../../common/utils.js'
import {
  baseSaveParamsSelector,
  baseShipperFormErrorsSelector,
  baseShipperInitialFormSelector,
  goToShipperSettings,
} from '../ShippersFunctions.js'
import className from '../../../../common/className.js'
import TextInput from '../../Common/TextInput.js'

export function setUpShipperForm({shipper, formName}) {
  return {
    formName,
    initialForm: {
      ...initialFormSelector(getState(), {shipper}),
      errorsSelector,
    },
  }
}

export function errorsSelector(state, {formName}) {
  const errors = baseShipperFormErrorsSelector(state, {formName})
  const form = formSelector(state, {
    formName,
  })

  if (!isPresent(form.postal_reporting_number)) {
    errors.postal_reporting_number = 'Shipper ID is required'
    errors.preventSave = true
  }

  if (!isPresent(form.client_id)) {
    errors.client_id = 'Client ID is required'
    errors.preventSave = true
  }

  return errors
}

export function initialFormSelector(state, {shipper}) {
  const vendorConfig = shipper?.vendor_config || {}

  return {
    ...baseShipperInitialFormSelector(state, {shipper, vendor: PITNEY_CBDS}),
    postal_reporting_number: vendorConfig.postal_reporting_number || '',
    client_id: vendorConfig.client_id || '',
    client_facility_id: vendorConfig.client_facility_id || '',
    facility_id: vendorConfig.facility_id || '',
  }
}

export async function saveShipperPitneyCBDS(formName) {
  const form = formSelector(getState(), {formName})
  let [shipperID, params] = baseSaveParamsSelector(getState(), {formName})

  const {postal_reporting_number, client_id, client_facility_id, facility_id} =
    form

  params = {
    ...params,
    postal_reporting_number,
    client_id,
    client_facility_id,
    facility_id,
  }

  updateForm(formName, {isSaving: true})

  try {
    const {json} = await (shipperID !== NEW_ID
      ? api.put(`/shipper/${shipperID}/`, params)
      : api.post('/shipper/', {...params, vendor: PITNEY_CBDS}))

    goToShipperSettings()

    setShipper(json)
  } catch (err) {
    showGlobalError(
      {
        summary: 'There was an error while saving shipper.',
      },
      err,
    )

    updateForm(formName, {isSaving: false})
  }
}

function PitneyCBDSForm({formName, form}) {
  const errors = useSelector((state) => form.errorsSelector(state, {formName}))
  const {
    id,
    postal_reporting_number,
    client_id,
    client_facility_id,
    facility_id,
    isSaving,
  } = form

  return (
    <div className="settings-list-item-form-wrap clear-both">
      <div className="medium-6 columns">
        <fieldset>
          <ul className="form-list margin-bottom-0">
            {id !== NEW_ID && <Nickname formName={formName} />}
            <li
              className={className`form-list-item bigger margin-bottom-20 ${{'margin-top-20 divider--top': form.id !== NEW_ID}}`}
            >
              <TextInput
                label="Shipper ID"
                name="postal_reporting_number"
                value={postal_reporting_number}
                onChange={(event) =>
                  updateForm(formName, {
                    postal_reporting_number: event.target.value,
                    hasChanged_postal_reporting_number: true,
                  })
                }
                errorMessage={
                  form.hasChanged_postal_reporting_number &&
                  errors.postal_reporting_number
                }
                required
              />
            </li>
            <li className="form-list-item bigger margin-bottom-20">
              <TextInput
                label="Client ID"
                name="client_id"
                value={client_id}
                onChange={(event) =>
                  updateForm(formName, {
                    client_id: event.target.value,
                    hasChanged_client_id: true,
                  })
                }
                errorMessage={form.hasChanged_client_id && errors.client_id}
                required
              />
            </li>
            <li className="form-list-item bigger margin-bottom-20">
              <TextInput
                label="Client Facility ID"
                name="client_facility_id"
                value={client_facility_id}
                onChange={(event) =>
                  updateForm(formName, {
                    client_facility_id: event.target.value,
                    hasChanged_client_facility_id: true,
                  })
                }
                errorMessage={
                  form.hasChanged_client_facility_id &&
                  errors.client_facility_id
                }
              />
            </li>
            <li className="form-list-item bigger margin-bottom-20">
              <TextInput
                label="Carrier Facility ID"
                name="facility_id"
                value={facility_id}
                onChange={(event) =>
                  updateForm(formName, {
                    facility_id: event.target.value,
                    hasChanged_facility_id: true,
                  })
                }
                errorMessage={form.hasChanged_facility_id && errors.facility_id}
              />
            </li>
            {id !== NEW_ID && <ShippingMethods formName={formName} />}
            <li className="list__item list__item--no-style">
              <ButtonPrimary
                onClick={() => saveShipperPitneyCBDS(formName)}
                isDisabled={errors.preventSave}
                isLoading={isSaving}
              >
                {id !== NEW_ID ? 'Save' : 'Create'}
              </ButtonPrimary>
              <ButtonSecondary onClick={() => goToShipperSettings()}>
                Cancel
              </ButtonSecondary>
            </li>
          </ul>
        </fieldset>
      </div>
      {id === NEW_ID && (
        <div className="medium-5 columns">
          <fieldset>
            <div className="alert margin-bottom-10 alert--standard">
              <dl className="list">
                <dt className="list__title fs-00 lh-md margin-bottom-5">
                  Need help setting up your Pitney Cross-Border Delivery Service
                  account?
                </dt>
                <dd className="list__item fs-n0 margin-bottom-3">
                  <div className="margin-bottom-3">
                    To get the process started, please contact Pitney Bowes
                    directly and let them know you’re an Ordoro customer.
                  </div>
                  <div>
                    <strong>
                      <a
                        className="btn--link mid meta-ups-setup-help"
                        href="https://support.ordoro.com/how-do-i-set-up-cbds-in-ordoro/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        More info →
                      </a>
                    </strong>
                  </div>
                </dd>
              </dl>
            </div>
          </fieldset>
        </div>
      )}
    </div>
  )
}

export default onlyIfAutoForm(PitneyCBDSForm, setUpShipperForm)
